import { useState, useEffect, useRef, useCallback } from 'react';
import cooldownManager from 'src/utils/cooldownManager';

export function useCooldown(key, durationMs) {
    const initialStatus = cooldownManager.isOnCooldown(key);
    const [isOnCooldown, setIsOnCooldown] = useState(initialStatus.active);
    const [remainingSeconds, setRemainingSeconds] = useState(
        Math.ceil(initialStatus.remainingMs / 1000)
    );
    const intervalRef = useRef(null);

    const stopInterval = useCallback(() => {
        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const startInterval = useCallback(() => {
        stopInterval();
        intervalRef.current = setInterval(() => {
            const status = cooldownManager.isOnCooldown(key);
            if (!status.active) {
                setIsOnCooldown(false);
                setRemainingSeconds(0);
                stopInterval();
            } else {
                setRemainingSeconds(Math.ceil(status.remainingMs / 1000));
            }
        }, 1000);
    }, [key, stopInterval]);

    // If already on cooldown on mount (e.g. after remount), start interval
    useEffect(() => {
        if (cooldownManager.isOnCooldown(key).active) {
            startInterval();
        }
        return stopInterval;
    }, [key, startInterval, stopInterval]);

    const triggerCooldown = useCallback(() => {
        cooldownManager.startCooldown(key, durationMs);
        const status = cooldownManager.isOnCooldown(key);
        setIsOnCooldown(true);
        setRemainingSeconds(Math.ceil(status.remainingMs / 1000));
        startInterval();
    }, [key, durationMs, startInterval]);

    const triggerCooldownByDateRange = useCallback((startDate, endDate) => {
        cooldownManager.startCooldownByDateRange(key, startDate, endDate);
        const status = cooldownManager.isOnCooldown(key);
        setIsOnCooldown(true);
        setRemainingSeconds(Math.ceil(status.remainingMs / 1000));
        startInterval();
    }, [key, startInterval]);

    const resetCooldown = useCallback(() => {
        cooldownManager.clearCooldown(key);
        setIsOnCooldown(false);
        setRemainingSeconds(0);
        stopInterval();
    }, [key, stopInterval]);

    return { isOnCooldown, remainingSeconds, triggerCooldown, triggerCooldownByDateRange, resetCooldown };
}
